import React, {
    MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    MenuItem,
    Typography,
    MenuList,
    Popover,
    Stack,
    Box,
} from '@mui/material';
import {
    ResultObject,
    ResultObjectItem,
    SearchResult as ResultType,
} from '../../api/types/search';
import camelCase from 'lodash/camelCase';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../contexts/TranslationContext';

export type ResultRef = {
    open: () => void;
    close: () => void;
    selector: {
        up: () => void;
        down: () => void;
        select: () => void;
    };
};

type SearchResultProps = {
    anchorEl: MutableRefObject<HTMLElement | undefined>;
    resultRef?: MutableRefObject<ResultRef | undefined>;
    searchResults?: ResultType;
    onItemSelected?: (selectedItem: any) => void;
    normalListPlaceholder?: string;
    normalListKey?: string;
    openOnResult?: boolean;
    maxHeight?: number | string;
};

type SectionItemProps = {
    item: ResultObjectItem | string;
    type: string;
    onClick: (type: string, item: ResultObjectItem | string) => void;
    highlighted?: boolean;
};

function firstCharUpper(string: string) {
    return `${string.substring(0, 1).toUpperCase()}${string.substring(1)}`;
}

function mergePaginationObjectToResult(
    type: string,
    keyName: string = 'type',
    data: any,
) {
    const keys = Object.keys(data);
    const values: Array<string> = Object.values(data);

    let result: ResultObject = { [type]: [] };
    for (let i = 0; i < keys.length; i++) {
        result[type].push({
            item_name: keys[i],
            [keyName]: values[i],
        });
    }

    return result;
}

function getOverallIndexItem(
    results?: ResultType,
    overallIndex: number = 0,
):
    | {
          type: string;
          item: ResultObjectItem;
      }
    | undefined {
    if (!results) {
        return undefined;
    }
    let index = 0;
    for (const type of Object.keys(results?.results ?? {})) {
        const items = results?.results?.[type];
        if (items) {
            for (const item of items) {
                if (index === overallIndex) {
                    return { type, item };
                }
                index++;
            }
        }
    }
    return undefined;
}

function getItemCount(results?: ResultType): number {
    if (!results) {
        return 0;
    }
    let index = -1;
    for (const type of Object.keys(results?.results ?? {})) {
        index = index + (results?.results?.[type]?.length ?? 0);
    }
    return index;
}

function SectionItem({
    type,
    item,
    onClick,
    highlighted = false,
}: SectionItemProps) {
    const toString = () => {
        try {
            const items = [];
            if (typeof item === 'string') {
                items.push(type);
            } else {
                for (const propName of Object.keys(item ?? {})) {
                    if (
                        propName !== 'id' &&
                        propName !== 'item_name' &&
                        item?.[propName] &&
                        typeof item?.[propName] !== 'object'
                    ) {
                        const label = camelCase(propName);
                        items.push(
                            `${firstCharUpper(label)}: ${item?.[propName]}`,
                        );
                    }
                }
            }
            return items.join(', ');
        } catch (e) {
            return '';
        }
    };

    const subStrings = toString();

    return (
        <MenuItem
            dense
            onClick={() => onClick(type, item)}
            selected={highlighted}
            sx={{ minHeight: 50 }}>
            <Stack width="100%">
                <Typography color="primary" variant="subtitle1">
                    {typeof item === 'string' ? item : item?.item_name}
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    flex={1}
                    className="cutTextEnd">
                    {subStrings.length > 0 ? `(${subStrings})` : ``}
                </Typography>
            </Stack>
        </MenuItem>
    );
}

export default function SearchResult({
    anchorEl,
    resultRef,
    searchResults,
    onItemSelected,
    openOnResult = true,
    normalListPlaceholder = '0',
    maxHeight,
    normalListKey,
}: SearchResultProps) {
    const containerRef = useRef<HTMLDivElement>();

    const navigate = useNavigate();
    const { translate } = useTranslation();

    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState<number>();

    const maxItems = getItemCount(searchResults);

    useEffect(() => {
        if (searchResults && searchResults.max_length > 0 && openOnResult) {
            setOpen(true);
        }
    }, [searchResults]);

    useEffect(() => {
        if (highlighted && containerRef.current) {
            containerRef.current.scrollTo({
                top: (highlighted / 1.2) * 50,
                behavior: 'smooth',
            });
        }
    }, [highlighted, containerRef]);

    const getSelector = useCallback(() => {
        const up = () => {
            setHighlighted(draft => {
                return typeof draft !== 'undefined' && draft >= 0
                    ? draft - 1
                    : 0;
            });
        };

        const down = () => {
            setHighlighted(draft => {
                return typeof draft !== 'undefined' && draft < maxItems
                    ? draft + 1
                    : 0;
            });
        };

        const select = () => {
            setOpen(false);
            const result = getOverallIndexItem(searchResults, highlighted);
            setHighlighted(undefined);
            if (result) {
                navigateToElement(result.type, result.item);
            }
        };

        return {
            up,
            down,
            select,
        };
    }, [searchResults, highlighted]);

    useEffect(() => {
        const open = () => {
            setOpen(true);
        };
        const close = () => {
            setOpen(false);
            setHighlighted(undefined);
        };

        if (resultRef) {
            resultRef.current = {
                open,
                close,
                selector: getSelector(),
            };
        }
    }, [resultRef, getSelector]);

    const navigateToElement = (
        type: string,
        item: ResultObjectItem | string,
    ) => {
        setOpen(false);
        setHighlighted(undefined);

        if (typeof item !== 'string' && item?.id) {
            switch (type) {
                case 'file_metadata':
                    navigate(`/cms/files?id=${item.file_id}`);
                    break;
                case 'partner':
                case 'network':
                case 'cronjob':
                    navigate(`/${type}/${item.id}`);
                    break;
                case 'page':
                    navigate(`/cms/pages/${item.id}/edit/${item.locale}`);
                    break;
            }
        }

        if (onItemSelected) {
            onItemSelected(item);
        }
    };

    const elements =
        searchResults?.results ??
        (searchResults?.data
            ? mergePaginationObjectToResult(
                  normalListPlaceholder,
                  normalListKey,
                  searchResults?.data,
              )
            : {});

    const renderSection = (sectionKey: string, index: number) => {
        const item = elements[sectionKey];

        if (!item || typeof item === 'undefined') return null;

        const renderItem = (
            subItem: ResultObjectItem | string,
            subIndex: number,
        ) => {
            overallIndex++;
            return (
                <SectionItem
                    key={`item-${index}-${subIndex}`}
                    type={sectionKey}
                    item={subItem}
                    highlighted={overallIndex === highlighted}
                    onClick={navigateToElement}
                />
            );
        };

        return [
            <Box
                key={`headline-${index}`}
                sx={{
                    px: 2,
                    py: 1,
                    position: 'sticky',
                    top: 0,
                    zIndex: index + 2,
                    background: theme => theme.palette.divider,
                }}>
                <Typography variant="overline">
                    {translate(`models.entity.${sectionKey}`)}
                </Typography>
            </Box>,
            item.map(renderItem),
        ];
    };

    const renderNoResult = () => (
        <MenuItem sx={{ height: 50 }}>
            <Typography>{translate('form.general.no_result')}</Typography>
        </MenuItem>
    );

    if (!anchorEl.current) {
        return null;
    }

    const sectionKeys = Object.keys(elements);

    let overallIndex = -1;
    return (
        <Popover
            PaperProps={{
                ref: (ref: any) => {
                    if (ref) {
                        containerRef.current = ref;
                    }
                },
                sx: {
                    maxHeight: maxHeight ?? 'calc(100vh - 200px)',
                    width: anchorEl.current?.clientWidth,
                },
            }}
            slotProps={{
                root: {
                    style: {
                        overflow: 'hidden',
                    },
                },
            }}
            anchorEl={anchorEl.current}
            open={open}
            disableAutoFocus
            disableEnforceFocus
            closeAfterTransition
            disableScrollLock={false}
            hideBackdrop
            onClose={() => setOpen(false)}
            sx={{ width: '100%' }}>
            <MenuList
                sx={{ p: 0, height: '100%', width: '100%' }}
                disableListWrap>
                {sectionKeys.length > 0
                    ? sectionKeys.map(renderSection)
                    : renderNoResult()}
            </MenuList>
        </Popover>
    );
}
